// Search History Utility
// Manages persistence of search queries in localStorage

const STORAGE_KEY = 'jira_search_history';
const MAX_HISTORY_ITEMS = 50;

export const searchHistoryUtils = {
  /**
   * Get all search history items
   * @returns {Array} Array of search history objects
   */
  getHistory: () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error reading search history:', error);
      return [];
    }
  },

  /**
   * Add a new search to history
   * @param {string} searchType - Type of search (e.g., 'ticket', 'account', 'salesforce')
   * @param {string} searchValue - The search value/query
   * @param {string} displayText - Text to display in history (optional, defaults to searchValue)
   */
  addToHistory: (searchType, searchValue, displayText = null) => {
    try {
      if (!searchValue || !searchType) return;

      const history = searchHistoryUtils.getHistory();
      const newItem = {
        id: Date.now() + Math.random(), // Unique ID
        type: searchType,
        value: searchValue,
        displayText: displayText || searchValue,
        timestamp: new Date().toISOString(),
        searchUrl: searchHistoryUtils.generateSearchUrl(searchType, searchValue)
      };

      // Remove duplicate if exists
      const filteredHistory = history.filter(
        item => !(item.type === searchType && item.value === searchValue)
      );

      // Add new item to beginning
      const updatedHistory = [newItem, ...filteredHistory];

      // Keep only last 50 items
      const trimmedHistory = updatedHistory.slice(0, MAX_HISTORY_ITEMS);

      localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedHistory));
    } catch (error) {
      console.error('Error saving search history:', error);
    }
  },

  /**
   * Generate the appropriate URL for a search
   * @param {string} searchType - Type of search
   * @param {string} searchValue - The search value
   * @returns {string} URL for the search
   */
  generateSearchUrl: (searchType, searchValue) => {
    switch (searchType) {
      case 'ticket':
        return `/jira/${searchValue}`;
      case 'account':
        return `/jira/relatedtickets/${searchValue}/AccountID`;
      case 'opportunity':
        return `/jira/relatedtickets/${searchValue}/OpportunityID`;
      case 'salesforce':
        return `/jira/salesforce/${searchValue}`;
      default:
        return `/jira/${searchValue}`;
    }
  },

  /**
   * Clear all search history
   */
  clearHistory: () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing search history:', error);
    }
  },

  /**
   * Remove a specific item from history
   * @param {string|number} itemId - ID of the item to remove
   */
  removeFromHistory: (itemId) => {
    try {
      const history = searchHistoryUtils.getHistory();
      const updatedHistory = history.filter(item => item.id !== itemId);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedHistory));
    } catch (error) {
      console.error('Error removing from search history:', error);
    }
  },

  /**
   * Update timestamp for an accessed item and move it to the top
   * @param {string|number} itemId - ID of the item that was accessed
   */
  updateAccessTime: (itemId) => {
    try {
      const history = searchHistoryUtils.getHistory();
      const itemIndex = history.findIndex(item => item.id === itemId);
      
      if (itemIndex !== -1) {
        // Update the timestamp
        const updatedItem = {
          ...history[itemIndex],
          timestamp: new Date().toISOString()
        };
        
        // Remove from current position and add to beginning
        const updatedHistory = [updatedItem, ...history.filter(item => item.id !== itemId)];
        
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedHistory));
        return updatedHistory;
      }
      return history;
    } catch (error) {
      console.error('Error updating access time:', error);
      return searchHistoryUtils.getHistory();
    }
  },

  /**
   * Get formatted display text for search history item
   * @param {Object} historyItem - Search history item
   * @returns {string} Formatted display text
   */
  getDisplayText: (historyItem) => {
    const typeLabels = {
      ticket: 'Ticket',
      account: 'Account',
      salesforce: 'Salesforce'
    };
    
    const typeLabel = typeLabels[historyItem.type] || historyItem.type;
    const timeAgo = searchHistoryUtils.getTimeAgo(historyItem.timestamp);
    
    return `${typeLabel}: ${historyItem.displayText} (${timeAgo})`;
  },

  /**
   * Get relative time string
   * @param {string} timestamp - ISO timestamp
   * @returns {string} Relative time (e.g., "2 hours ago", "recently searched")
   */
  getTimeAgo: (timestamp) => {
    try {
      const now = new Date();
      const searchTime = new Date(timestamp);
      const diffMs = now - searchTime;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return 'just now';
      if (diffMins < 60) return `${diffMins}min ago`;
      if (diffHours < 24) return `${diffHours}hr ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      
      return 'recently searched';
    } catch (error) {
      return 'recently searched';
    }
  }
};

export default searchHistoryUtils;